<script setup>
import { computed, provide, ref, shallowRef, watch } from 'vue'
import DOMPurify from 'dompurify'
import { useToggle } from '@vueuse/core'

import { commons } from '@provise/lib/transformer'
import { useApi } from '@provise/composables/api'
import { useMessageStack } from '@provise/stores/messagestack'

import Mod from '@provise/components/mod.vue'

import CommunicationItem from './communicationItem.vue'
import CommunicationForm from './form.vue'

defineEmits(['close'])

const props = defineProps({
  complaintId: {
    type: Number,
    required: true,
  },
  communicationId: {
    type: Number,
    default: null,
  },
})

const model = ref({
  email_addresses: [],
})
const communications = ref([])
const itemsMode = shallowRef('all') // all | internal
const [isFormOpen, formToggle] = useToggle(false)
const communicationsUrl = computed(() => `complaints/${props.complaintId}/communication`)

//----------------------------

const { push: notify } = useMessageStack()
const { refresh: refreshCommunications } = useApi({
  url: communicationsUrl,
  watch: [communicationsUrl],
  model: communications,
  pick: pickCommunications,
  immediate: true,
})

const communicationsFiltered = computed(() => {
  if (itemsMode.value == 'all') {
    return communications.value
  }
  if (itemsMode.value == 'external') {
    return communications.value.filter(c => c.channel?.key != 'internal')
  }
  return communications.value.filter(c => c.channel?.key == 'internal')
})
const hasCommunications = shallowRef(false)
watch(communications, cs => {
  formToggle(!cs.length)
  hasCommunications.value = cs.length
})
watch(
  model,
  mv => {
    if (mv.id) {
      formToggle(true)
    }
  },
  { immediate: true }
)
watch(
  isFormOpen,
  fo => {
    if (!fo) {
      model.value = { email_addresses: [] }
    } else {
      if (!model.value?.id) {
        model.value = { email_addresses: [] }
      }
    }
  },
  { flush: 'post' }
)

provide('form:editable', shallowRef(true))

function pickCommunications(d) {
  let dr = d.data || []
  dr = dr.map(comItem => {
    const result = { ...comItem }
    if (comItem.date_created) {
      result.date_created = commons.dateLocal(comItem.date_created, 'Pp')
    }
    var str = DOMPurify.sanitize(comItem.email_text, { USE_PROFILES: { html: true } })
      .replaceAll('  ', '')
      .replaceAll('</', '\n</')
      .replace(/<\/?[^>]+(>|$)/g, '')
      .trim()
    result.email_text = '<pre>' + (str.length > 150 ? str.substring(0, 150) + '…' : str) + '</pre>'
    return result
  })
  return dr.sort((a, b) => (a.id < b.id ? 1 : -1))
}

function onGetItem(item) {
  model.value = item
}

async function onSaveItem(item) {
  notify('Коммуникация сохранена')
  await refreshCommunications()
  model.value = item

  const messCommunications = communications.value.filter(c => c.repeat_mess)
  if (messCommunications.length > 1)
    alert(
      'Вы отправляете уже третье или более повторное сообщение. Возможно, обращение надо закрыть?'
    )
}
</script>
<template>
  <Mod @hidden="$emit('close')">
    <template #header>
      <div class="hstack flex-grow-1 align-items-center">
        <h5 class="mb-0">Коммуникация</h5>
        <button
          type="button"
          class="btn btn-link"
          @click.stop="formToggle(true)"
          v-if="!isFormOpen"
        >
          <i class="bi bi-plus-circle" />
        </button>
        <div class="ms-auto hstack" v-if="hasCommunications">
          <button
            class="btn btn-link"
            type="button"
            :disabled="itemsMode == 'all'"
            @click="itemsMode = 'all'"
          >
            Все сообщения
          </button>
          <button
            class="btn btn-link ms-auto"
            type="button"
            :disabled="itemsMode == 'internal'"
            @click="itemsMode = 'internal'"
          >
            Только внутренние
          </button>
          <button
            class="btn btn-link ms-auto"
            type="button"
            :disabled="itemsMode == 'external'"
            @click="itemsMode = 'external'"
          >
            Только внешние
          </button>
        </div>
      </div>
    </template>
    <template #default>
      <div class="vstack gap-3">
        <div class="card" v-if="isFormOpen">
          <div class="card-header d-flex">
            Cообщение
            <button
              type="button"
              class="ms-auto btn-close"
              @click="formToggle(false)"
              v-if="hasCommunications"
            />
          </div>
          <div class="card-body">
            <CommunicationForm
              v-model="model"
              @save="onSaveItem"
              @close="formToggle(false)"
              :url="communicationsUrl"
              :complaint-id="complaintId"
            />
          </div>
        </div>
        <CommunicationItem
          v-for="item of communicationsFiltered"
          :key="item.id"
          :model="item"
          :class="{ current: model.id == item.id }"
          @open="onGetItem(item)"
        />
      </div>
    </template>
  </Mod>
</template>

<style scoped>
.current {
  background-color: rgba(0, 0, 0, 0.1);
}
</style>
